<template>
  <div>
    <div v-if="isFetchingUnits">
      <v-skeleton-loader v-for="i in 3" :key="i" type="list-item" />
    </div>
    <ValidationObserver ref="approvalChainForm" v-else>
      <ValidationProvider v-slot="{ errors }" rules="required">
        <Select
          v-model="approvalChainTemplateData.organisationUnitId"
          label="Organisation Unit"
          item-text="name"
          item-value="id"
          :items="filteredOrganisations"
          :loading="isFetchingUnits"
          :errorMessages="getErrorMessages(errors)"
          autocomplete
        />
      </ValidationProvider>
      <div
        class="list-type-selection"
        @click="showAdvanceOptions = !showAdvanceOptions"
      >
        <v-icon color="primary">
          {{ `mdi-chevron-${showAdvanceOptions ? "down" : "right"}` }}
        </v-icon>
        <span class="list-type-text">
          Advance Options
        </span>
      </div>
      <div v-if="showAdvanceOptions">
        <Select
          v-model="approvalChainTemplateData.user_id"
          label="Specific to User"
          item-text="label"
          item-value="id"
          :items="formattedUsers"
          :loading="isFetchingUsers"
          autocomplete
        />
        <div class="d-flex align-baseline flex-wrap">
          <v-text-field
            v-money="moneyConfig"
            label="Cost Threshold"
            aria-label="Salary Band Lower"
            prefix="£"
            v-model="approvalChainTemplateData.totalCostLower"
          />
          <label class="mx-2">to</label>
          <v-text-field
            aria-label="Salary Band Upper"
            v-money="moneyConfig"
            prefix="£"
            v-model="approvalChainTemplateData.totalCostUpper"
          />
        </div>
      </div>
      <h3 class="my-2">Approvers</h3>
      <div v-if="!autoApproved">
        <ValidationProvider
          v-for="(approvalChainLevel, index) in approvalChainTemplateLevels"
          :key="index"
          v-slot="{ errors }"
          rules="required"
        >
          <Select
            :label="`Level ${approvalChainLevel.level}`"
            item-text="label"
            item-value="id"
            :items="formattedUsers"
            :loading="isFetchingUsers"
            :errorMessages="getErrorMessages(errors)"
            :value="approvalChainLevel.users"
            :appendOuterIcon="
              isLastIndex(index) && index !== 0 ? `mdi-close` : null
            "
            @change="
              $event => handleUpdateApprovers(approvalChainLevel.level, $event)
            "
            @clickOuterIcon="removeLevel(approvalChainLevel.level)"
            multiple
            autocomplete
            chips
          >
            <template v-slot:selection="{ data }">
              <v-chip>
                {{ data.item.label }}
              </v-chip>
            </template>
          </Select>
        </ValidationProvider>
        <PrimaryButton full-width light @click.native="addLevel">
          Add Approval Level
          <v-icon>mdi-plus</v-icon>
        </PrimaryButton>
      </div>
      <v-checkbox
        v-model="autoApproved"
        class="my-2"
        label="Auto-approve roles"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { createNamespacedHelpers } from "vuex";
import { filter, isEmpty, map, reduce, size } from "lodash";
import Select from "@/components/common/Select";
import { ORGANISATION_UNITS_NAMESPACE } from "@/store/modules/organisations/organisation-units";
import { FETCH_CLIENT_ONLY } from "@/store/modules/organisations/organisation-units/actions";
import { copyState } from "@/utils/helpers";
import { FETCH_USERS } from "@/store/modules/users/actions";
import { USERS_NAMESPACE } from "@/store/modules/users";
import { getFullName } from "@/utils/users";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import {
  getAmountInApiFormat,
  getMonetaryAsNumber,
  getMonetaryDisplayFormat
} from "@/utils/helpers";

const {
  mapActions: mapOrganisationActions,
  mapState: mapOrganisationState
} = createNamespacedHelpers(ORGANISATION_UNITS_NAMESPACE);
const {
  mapActions: mapUserActions,
  mapState: mapUserState
} = createNamespacedHelpers(USERS_NAMESPACE);

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    Select,
    PrimaryButton
  },
  props: {
    approvalChainTemplate: Object
  },
  created() {
    if (this.approvalChainTemplate) {
      this.adjustApprovalChainTemplateData();
    }
    this.autoApproved = isEmpty(this.approvalChainTemplateLevels);
    if (isEmpty(this.clientOrganisationUnits)) {
      this.fetchOrganisations({ filter: { organisation_type: "client" } });
    }
    const filter = {
      role: ["client booking approver"]
    };
    this.fetchUsers({ filter, page: { size: 10000 } });
  },
  data() {
    return {
      approvalChainTemplateData: {
        client: {},
        user: {},
        user_id: ""
      },
      showValidationErrors: false,
      showAdvanceOptions: false,
      autoApproved: false,
      moneyConfig: { precision: 2 },
      approvalChainTemplateLevels: [{ level: 1, users: [] }]
    };
  },
  computed: {
    ...mapUserState(copyState(["users", "isFetchingUsers"])),
    ...mapOrganisationState(
      copyState(["clientOrganisationUnits", "isFetchingUnits"])
    ),
    formattedUsers() {
      return map(this.users, user => {
        return { ...user, label: getFullName(user) };
      });
    },
    approvers() {
      return reduce(
        this.approvalChainTemplateLevels,
        (approvalChainTemplateLevelUsers, approvalChainTemplateLevel) => {
          const { users, level } = approvalChainTemplateLevel;
          map(users, userId => {
            approvalChainTemplateLevelUsers.push({ level, userId });
          });
          return approvalChainTemplateLevelUsers;
        },
        []
      );
    },
    filteredOrganisations() {
      return filter(
        this.clientOrganisationUnits,
        ({ status }) => status === "live"
      );
    }
  },
  methods: {
    ...mapOrganisationActions({
      fetchOrganisations: FETCH_CLIENT_ONLY
    }),
    ...mapUserActions({ fetchUsers: FETCH_USERS }),
    adjustApprovalChainTemplateData() {
      this.approvalChainTemplateLevels = map(
        this.approvalChainTemplate.approvalChainTemplateLevels,
        level => {
          const { users } = level;
          const formattedUsers = map(users, ({ userId }) => String(userId));
          return { ...level, users: formattedUsers };
        }
      );
      this.approvalChainTemplateData = {
        ...this.approvalChainTemplate,
        totalCostLower: getMonetaryDisplayFormat(
          this.approvalChainTemplate.totalCostLower
        ),
        totalCostUpper: getMonetaryDisplayFormat(
          this.approvalChainTemplate.totalCostUpper
        ),
        user_id:
          this.approvalChainTemplate.user_id &&
          String(this.approvalChainTemplate.user_id),
        organisationUnitId: String(
          this.approvalChainTemplate.organisationUnitId
        )
      };
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    },
    async getApprovalChainTemplateData() {
      return {
        totalCostLower: getAmountInApiFormat(
          getMonetaryAsNumber(this.approvalChainTemplateData.totalCostLower)
        ),
        totalCostUpper: getAmountInApiFormat(
          getMonetaryAsNumber(this.approvalChainTemplateData.totalCostUpper)
        ),
        approvers: this.autoApproved ? [] : this.approvers,
        user_id: this.approvalChainTemplateData.user_id,
        organisationUnitId: this.approvalChainTemplateData.organisationUnitId,
        client: { id: this.approvalChainTemplateData.organisationUnitId },
        id: this.approvalChainTemplateData.id
      };
    },
    async validateForm() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.approvalChainForm.validate();
      if (isValid) {
        const data = await this.getApprovalChainTemplateData();
        this.$emit("onSubmit", data);
      }
    },
    handleUpdateApprovers(level, users) {
      this.approvalChainTemplateLevels = map(
        this.approvalChainTemplateLevels,
        approvalChainTemplateLevel => {
          if (approvalChainTemplateLevel.level === level) {
            approvalChainTemplateLevel.users = users;
          }
          return approvalChainTemplateLevel;
        }
      );
    },
    addLevel() {
      const level = size(this.approvalChainTemplateLevels) + 1;
      this.approvalChainTemplateLevels = [
        ...this.approvalChainTemplateLevels,
        {
          level,
          users: []
        }
      ];
    },
    isLastIndex(index) {
      return index == size(this.approvalChainTemplateLevels) - 1;
    },
    removeLevel(levelToBeRemoved) {
      this.approvalChainTemplateLevels = filter(
        this.approvalChainTemplateLevels,
        ({ level }) => level !== levelToBeRemoved
      );
    }
  }
};
</script>

<style lang="scss"></style>
